import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { TicketBoard as Board, TicketBoardCard, TicketKarte, TicketSpalte, TicketZeile, Unternehmen, FremdKarte }
  from "@kundeninfo/api-client";
import { useBestaetigung, useTelefon } from "@kundeninfo/ui";
import { Board as MeBoard, Boardspalte, Chip, Hinweis, Knopf, Optionen, Werkzeuggruppe, Werkzeugleiste, ziehbar }
  from "@kundeninfo/ui/me-design";
import type { BoardElement, QuittungSteuerung } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { keepInView } from "../../scroll.js";
import { TicketZeitleiste } from "./TicketZeitleiste.js";
import type { AblageZiel } from "./TicketZeitleiste.js";
import { TicketKarteAnsicht } from "./TicketKarte.js";
import { SpaltenDialog } from "./SpaltenDialog.js";
import { TicketRaster } from "./TicketRaster.js";
import { TicketFilterleiste, anzahlAktiverFilter, filterAnfrage } from "./TicketFilterleiste.js";
import { berlinerTag, freieTage, popupFuerTag, tagBeschriftung, zeileFuer, zielAttribute as ablageAttribute }
  from "./ticketFormat.js";
import { ziehOptionen, zugLaeuft } from "./ticketZiehen.js";
import type { ZiehStand } from "./ticketZiehen.js";
import { ticketReceipt } from "./receipts.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";
import { isOpen } from "./hierarchy.js";
import { confirmOpenChildren } from "./openChildren.js";
import { TicketVerschieben } from "./TicketVerschieben.js";
import type { TicketsClient } from "./ticketsClient.js";
import { LEERER_FILTER, gemerkteAnsicht, leseFilter, leseOffen, leseZu, merkeAnsicht, merkeFilter, merkeOffen, merkeZu }
  from "./boardSpeicher.js";
import type { BoardFilter } from "./boardSpeicher.js";
import { FREMD_SPALTE_ID, fremdPerson, useFremdtickets } from "./fremdSicht.js";
import { FremdSpalte } from "./FremdSpalte.js";
import { FremdKarteAnsicht } from "./FremdKarte.js";
import { FremdDialog } from "./FremdDialog.js";
import { SYMBOL_PLUS } from "./ticketSymbole.js";
import type { FremdticketsClient } from "../fremd/fremdClient.js";
import "./Tickets.css";

/**
 * DAS BOARD (Spec „Board"): Spalten nebeneinander auf `me-board`/`me-boardspalte` (me-design
 * a885596, Entscheid 54: Breite 320–640 px, klebender Kopf mit Name, Zähler und Klappknopf, jede
 * Spalte rollt für sich, am Telefon genau eine Spalte mit der Segmentleiste des Elements), je
 * Spalte die Zeitleiste,
 * Werkzeugleiste (me-werkzeugleiste, F129, Entscheid 199 / me-design 61 C) mit Unternehmen (115),
 * Kunde, Zuständig, „nur verknüpfte", Darstellung je Spalte/Raster (F19), „Neue Spalte" und
 * „+ Aufgabe"; Stufen, „…", Telefonblatt und „Zurücksetzen" macht das Element. Lädt beim Öffnen, bei
 * Fokus-Rückkehr, alle 60 s und nach Mitternacht neu (F20); das Rollfeld bleibt dabei dasselbe
 * Element, damit die Scrollposition stehen bleibt (Hausregel). Filter, Darstellung und Auf-/
 * Zuklappen (der Zeitleiste und der Spalte) merkt sich der Browser (F19, `boardSpeicher.ts`).
 * Passen die Spalten mit 320 px nicht nebeneinander, starten erledigte Spalten ohne Zeitleiste
 * zugeklappt (Entscheid 156, ResizeObserver am Board); ein gemerkter Zustand der Spalte geht vor.
 * Die überfälligen Tickets zählt die Fehlerpille der Spalte (`fehler`, me-design 79d237b, auch
 * zugeklappt sichtbar); `randlos` lässt Zeitleisten-Bänder bis an den Rand laufen, die Abstände
 * der Karten setzt `Tickets.css`; im Slot `menue` nur ⋯, die Dichte der Zeitleiste steht im
 * Spaltendialog (Entscheid 155).
 * Ziehen am Griff über die Ziehhilfen aus me-design (`ziehbar`, F126; Regeln in `ticketZiehen.ts`,
 * Kopf-Zone „Datum bleibt", 206; am Telefon ohne Griff und ohne Ziehen, E457) und „Verschieben …" (F22) gehen an die Ablage;
 * die Karte steht sofort am Ziel, die Antwort des Servers gleicht ab, 409 lädt neu (Review-
 * Fokus 2). Danach die Quittung mit „Rückgängig" als Gegenbuchung (F23) über `ticketReceipt` (`quittung()`, Spec
 * Teilaufgaben R22, Festlegung 8: am body, erst wenn „Verschieben …" zu ist). In eine DONE-Spalte fragt die Ablage bei
 * offenen Teilaufgaben nach (E435, `openChildren.ts`).
 * Fokus (Spec Teilaufgaben R26, E434): Klick auf die Striche einer Karte mit Kindern zeigt Hauptkarte und direkte Kinder
 * auch gegen den Filter in ihren Spalten (`listFocusCards`), dimmt alles andere (`Tickets.css`) und zählt je Spalte im
 * Slot `menue` vor ⋯ („Fokus 2 +1", Festlegung 7); am Telefon trägt der Spaltenzähler die Zahl in Klammern (R27). Eingeklappte
 * Spalten der Hauptkarte und ihrer Kinder klappt der Fokus auf und beim Ende wieder zu (E453, `focusColumns`; das Gedächtnis `zu` bleibt).
 * Die Filter teilt das Board mit der Liste (`TicketFilterleiste.tsx`, Ruling A4); das Raster
 * (`TicketRaster.tsx`, 118; Übergang T-1 Rest: eigenes Gitter) zeichnet in einem eigenen Rollfeld mit
 * denselben Karten und Ablagezielen; dort meldet sich das Board mit `spalten: '[data-tag]'` an.
 */

export { ANSICHT_SCHLUESSEL } from "./boardSpeicher.js";
export type { BoardFilter } from "./boardSpeicher.js";

export { filterAnfrage } from "./TicketFilterleiste.js";

const BERLIN = new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", hour: "2-digit",
  minute: "2-digit", second: "2-digit", hourCycle: "h23" });

/** Wanduhr-Rechnung: An Tagen der Zeitumstellung liegt der Timer eine Stunde daneben (Gesamtreview
 *  M11). Feuert er zu früh, bleibt `heute` gleich und der Effekt stellt sich nicht neu — der
 *  60-s-Takt lädt dann binnen einer Minute nach Mitternacht nach, die Zeitleiste rückt trotzdem. */
export function msBisMitternachtBerlin(jetzt: Date): number {
  const t = BERLIN.formatToParts(jetzt);
  const w = (typ: string) => Number(t.find((x) => x.type === typ)?.value ?? "0");
  return ((24 - w("hour")) * 3600 - w("minute") * 60 - w("second")) * 1000 - jetzt.getMilliseconds();
}

export function quittungsText(t: TicketKarte, spaltenName: string): string {
  if (t.resubmitDate === null) return `${t.ticketNumber} → ${spaltenName}`;
  const zeit = t.resubmitTime === null ? "" : ` ${t.resubmitTime}`;
  return `${t.ticketNumber} → ${spaltenName}, Wiedervorlage ${tagBeschriftung(t.resubmitDate)}${zeit}`;
}

/** Die Zeile, auf die ein Ziel zeigt (−1: keine Zeile, etwa ein Datum jenseits der 30 Tage). */
function zielZeile(z: AblageZiel, zeilen: readonly TicketZeile[]): number {
  if (z.ziel === "JETZT") return 0;
  const zeile = z.ziel === "TAG" ? zeilen.find((x) => x.art === "TAG" && x.datum === z.datum)
    : zeilen.find((x) => x.art === z.ziel && x.von === z.datum);
  return zeile?.index ?? -1;
}

/** Am eigenen Platz abgelegt (eigene Spalte und Kopf, eigene Zeile oder derselbe Tag)? Dann keine
 *  Anfrage, keine Quittung — auch nicht für Jetzt auf einem überfälligen oder undatierten Ticket.
 *  Eine geänderte Uhrzeit am eigenen Platz ist trotzdem eine Ablage. Rein und synchron: der Zug
 *  meldet damit dem Helfer `'bleibt'` (me-design 84b8db8), bevor eine Anfrage losgeht. */
function amEigenenPlatz(t: TicketKarte, ziel: AblageZiel, zeilen: readonly TicketZeile[], heute: string): boolean {
  return ziel.columnId === t.columnId && ziel.uhrzeit === undefined && (ziel.ziel === "KOPF"
    || (ziel.ziel === "TAG" && ziel.datum === t.resubmitDate)
    || zielZeile(ziel, zeilen) === zeileFuer(t.resubmitDate, zeilen, heute));
}

/** Symbole der Darstellung und von „Neue Spalte“ im 16er-Raster (Story Struktur › Werkzeugleiste ›
 *  Boardkopf, Entscheid 61e) — `data-symbol` an den Segmenten bzw. am Punkt im „…“. */
const SYMBOL_JE_SPALTE = '<rect x="2" y="2.5" width="3.2" height="11" rx=".6"/><rect x="6.4" y="2.5" width="3.2" height="7" rx=".6"/><rect x="10.8" y="2.5" width="3.2" height="9" rx=".6"/>';
const SYMBOL_RASTER = '<rect x="2.5" y="2.5" width="4.5" height="4.5" rx=".6"/><rect x="9" y="2.5" width="4.5" height="4.5" rx=".6"/><rect x="2.5" y="9" width="4.5" height="4.5" rx=".6"/><rect x="9" y="9" width="4.5" height="4.5" rx=".6"/>';
const SYMBOL_NEUE_SPALTE = '<rect x="2" y="2.5" width="4" height="11" rx=".6"/><path d="M11 5v6M8 8h6"/>';

/** Mindestbreite der Spalten (Entscheid 54). */
const SPALTE_MIN = 320;

/** Lücke zwischen den Spalten und Innenabstand der Bahn (je Seite) in px. */
export type BahnMasse = { luecke: number; rand: number };
/** Rückfall, wenn das Element `bahnMasse` nicht kennt (wie me-board selbst: 12/4). */
const BAHN_VORGABE: BahnMasse = { luecke: 12, rand: 4 };

/** Liest `board.bahnMasse` (me-design a9a3e5a, aus dem gerechneten Stil der Bahn); fehlt die
 *  Eigenschaft oder ist sie unbrauchbar, gilt die Vorgabe 12/4. */
function bahnMasseVon(el: BoardElement): BahnMasse {
  const m = (el as BoardElement & { bahnMasse?: Partial<BahnMasse> }).bahnMasse;
  const zahl = (n: unknown, vorgabe: number) => (typeof n === "number" && Number.isFinite(n) ? n : vorgabe);
  return { luecke: zahl(m?.luecke, BAHN_VORGABE.luecke), rand: zahl(m?.rand, BAHN_VORGABE.rand) };
}

/**
 * Entscheid 156: Passen die Spalten mit ihrer Mindestbreite nicht nebeneinander, starten die
 * erledigten Spalten ohne Zeitleiste zugeklappt. Eine Breite von 0 heißt „noch nicht gemessen“
 * (nicht gelayoutet) — dann klappt nichts von selbst. Lücke und Rand der Bahn liefert me-board.
 */
export function spaltenZuEng(anzahl: number, breite: number, bahn: BahnMasse = BAHN_VORGABE): boolean {
  if (breite <= 0 || anzahl === 0) return false;
  return anzahl * SPALTE_MIN + (anzahl - 1) * bahn.luecke + 2 * bahn.rand > breite;
}

const startetZu = (s: TicketSpalte) => s.stateKind === "DONE" && s.timelineMode === "NONE";

type Quittung = { text: string; ticketId: string; version: number;
                  vorher: { columnId: string; resubmitDate: string | null; resubmitTime?: string | null };
                  /** Ansage nach gelungenem „Rückgängig“: „<Nr> zurück in <Spalte>“ (Ruling 1d-B). */
                  zurueck: string;
                  /** Nach einem Zug: `detail.rueckgaengig()` der Ziehhilfen (Ansage, Aufleuchten, F126). */
                  helfer?: () => boolean };

const GEAENDERT = "Das Ticket wurde inzwischen geändert.";
const LADEFEHLER = "Das Board konnte nicht geladen werden.";

export type TicketBoardProps = {
  client: TicketsClient; companies: readonly Unternehmen[]; ich: { id: string; name: string; istAdmin?: boolean };
  onOeffnen: (id: string) => void; onAnlegen: (companyId: string | null) => void; neuLaden?: number;
  /** Nachtrag 1f (F142): ohne Klient keine Fremdspalte (Tests, schmale Klienten). */
  fremd?: FremdticketsClient;
  /** F129 (61c, 61i): nach jedem geladenen Stand die offenen Tickets (Statusart ≠ DONE, mit Filtern)
   *  und der Tag des Servers — für den Zähler am Reiter „Board“ und das Datum der Reiterzeile. */
  onOffen?: (offen: number, heute: string) => void;
  /** „Im Board zeigen" (R26): Fokus auf `id` von außen; `seq` macht jede Bitte neu. */
  focusRequest?: { id: string; seq: number } | null;
  /** „Im Board zeigen" im Kopf der Randliste am Telefon (R27). */
  onShowInBoard?: (id: string) => void;
};

/** Was ein Klick NICHT als freie Boardfläche zählt (R26): Karten, Leiste, Dialoge, Hinweise und alles Bedienbare — im
 *  Pfad des Ereignisses, auch in Schattenbäumen (Klappknopf der Spalte, Segmentleiste des Telefons). */
const NICHT_FREI = '.tk, .tfk, me-werkzeugleiste, me-dialog, me-hinweis, me-optionen, button, a[href], input, select, '
  + 'textarea, [role="button"], [role="radio"], [role="tab"]';

/** Die Kennung der Karte, in der der Fokus gerade steht (nur innerhalb von `wurzel`), sonst null. */
function fokussierteKarte(wurzel: HTMLElement | null): string | null {
  const aktiv = document.activeElement;
  if (wurzel === null || aktiv === null || !wurzel.contains(aktiv)) return null;
  return aktiv.closest<HTMLElement>(".tk[data-ticket]")?.dataset.ticket ?? null;
}

/** Die Spalte (`me-boardspalte`), in der der Tastaturfokus gerade steht (nur innerhalb von `wurzel`), sonst null. */
function tastaturSpalte(wurzel: HTMLElement | null): string | null {
  const aktiv = document.activeElement;
  if (wurzel === null || aktiv === null || !wurzel.contains(aktiv)) return null;
  return aktiv.closest("me-boardspalte")?.id ?? null;
}

/** F121: Fokus auf den Öffnen-Griff der Karte `id` unter `wurzel`, ohne zu rollen; false, wenn sie fehlt. */
export function fokussiereKarte(wurzel: ParentNode, id: string): boolean {
  const karte = [...wurzel.querySelectorAll<HTMLElement>(".tk[data-ticket]")].find((k) => k.dataset.ticket === id);
  const griff = karte?.querySelector<HTMLElement>(".tk__oeffnen");
  if (griff === null || griff === undefined) return false;
  griff.focus({ preventScroll: true });
  return true;
}

export function TicketBoard(p: TicketBoardProps): ReactElement {
  const [board, setBoard] = useState<Board | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [filter, setFilter] = useState<BoardFilter>(() => leseFilter(p.companies.map((c) => c.id)));
  const [ansicht, setAnsicht] = useState<"spalten" | "raster">(gemerkteAnsicht);
  const telefon = useTelefon();
  const [telefonSpalte, setTelefonSpalte] = useState<string | null>(null);
  // Nachtrag 1f (F142): die Fremdtickets der Person nach der Sichtregel; eine eigene Spalte nur mit Konto.
  const fremdPersonId = p.fremd === undefined ? null : fremdPerson(filter, p.ich);
  const fremdStand = useFremdtickets(p.fremd, fremdPersonId, p.ich.id, p.neuLaden);
  const fremdSichtbar = fremdStand.daten !== null && fremdStand.daten.konten > 0;
  const [fremdDialog, setFremdDialog] = useState<string | null>(null);
  const [fremdMeldung, setFremdMeldung] = useState<string | null>(null);
  // F36: am Telefon kein Raster; die gemerkte Ansicht (`ansicht`) bleibt dabei unberührt.
  const wirksameAnsicht = telefon ? "spalten" : ansicht;
  // Fehlt die gewählte Spalte (gelöscht, neue Id), gilt die Eingangsspalte, sonst die erste; die Fremdspalte
  // ist am Telefon wählbar, solange sie steht.
  const aktiveTelefonSpalte = board === null ? "" : telefonSpalte === FREMD_SPALTE_ID && fremdSichtbar ? FREMD_SPALTE_ID
    : (board.spalten.find((s) => s.id === telefonSpalte)
      ?? board.spalten.find((s) => s.isInbox) ?? board.spalten[0])?.id ?? "";
  const [offen, setOffen] = useState<Record<string, boolean>>(leseOffen);
  const [zu, setZu] = useState<Record<string, boolean>>(leseZu);
  const [spaltenDialog, setSpaltenDialog] = useState<{ spalte: TicketSpalte | null } | null>(null);
  const boardRef = useRef<BoardElement | null>(null);
  // Breite des Boards (ResizeObserver) für Entscheid 156; 0 = noch nicht gemessen.
  const [boardBreite, setBoardBreite] = useState(0);
  // Lücke und Rand der Bahn, nach dem Einhängen und bei jeder Größenänderung vom Element gelesen.
  const [bahn, setBahn] = useState<BahnMasse>(BAHN_VORGABE);
  const rasterFeld = useRef<HTMLDivElement | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [verschieben, setVerschieben] = useState<TicketKarte | null>(null);
  // Welcher „Verschieben …“-Dialog gerade offen ist — für eine Antwort, die erst nach dem
  // Schließen eintrifft (ihr Befund gehört dann ans Board, nicht an ein Feld, das es nicht mehr gibt).
  const verschiebenOffen = useRef<string | null>(null);
  useEffect(() => { verschiebenOffen.current = verschieben?.id ?? null; }, [verschieben]);
  const [quittung, setQuittung] = useState<Quittung | null>(null);

  // Nur die jüngste Anfrage zeichnet: Ein schneller Filterwechsel darf nicht von der Antwort
  // auf den alten Filter überholt werden.
  const lauf = useRef(0);
  // F121, Review-Fokus 4: Steht der Fokus beim Eintreffen eines neuen Stands auf einer Karte, die
  // dabei die Spalte (oder die Zeile der Zeitleiste) wechselt, hängt React sie unter einem anderen
  // Elternteil neu ein und der Fokus fiele auf <body>. Gemerkt wird die Karte vor dem Zeichnen,
  // nach dem Zeichnen bekommt sie ihn einmalig zurück — beim Neuladen nach dem Panel ebenso wie
  // im 60-s-Takt.
  const sektion = useRef<HTMLElement | null>(null);
  const fokusRetten = useRef<string | null>(null);

  // R26: EIN Fokus zur Zeit — Hauptkarte und ihre direkten Kinder (ohne Filter, mit Sicht, ohne archivierte, E434).
  // Ansichtszustand, nicht gespeichert; das Neuladen hält ihn.
  const [fokus, setFokus] = useState<{ id: string; karten: TicketBoardCard[] } | null>(null);
  // Laufzähler je Fokus-Ziel (Abschluss-Review M2): `ziel` ist die Kennung, auf die zuletzt geschaltet wurde, auch solange
  // ihr Laden noch läuft. Nachgeladen wird das Ziel, nicht der gezeigte Fokus — der Takt überholt so keinen Wechsel A→B.
  // `switching`: ein Schalten (Striche, „Im Board zeigen“) lädt noch; seine Antwort ist frisch, das Neuladen des Boards
  // wartet auf sie, statt sie mit einem zweiten Abruf zu verwerfen (Re-Review der Abschluss-Fix-Runde).
  const fokusLauf = useRef<{ ziel: string | null; nr: number; switching: boolean }>({ ziel: null, nr: 0, switching: false });
  /** Lädt den Fokus auf `id`; es gilt nur die jüngste Anfrage. Eine leere Antwort (Hauptkarte fort) beendet ihn; scheitert
   *  ein Nachladen, bleibt er und der Hinweis sagt es — nur ein gescheitertes Schalten endet ohne Fokus. */
  const fokusLaden = useCallback(async (id: string, nachladen = false) => {
    const nr = fokusLauf.current.nr + 1;
    fokusLauf.current = { ziel: id, nr, switching: !nachladen };
    try {
      const r = await p.client.listFocusCards(id);
      if (nr !== fokusLauf.current.nr) return;
      fokusLauf.current.switching = false;
      if (r.items.length === 0) fokusBeenden();
      else {
        if (fokusRef.current !== null && fokusRef.current.id !== id) tastaturSpalteMerken();
        setFokus({ id, karten: r.items });
      }
    } catch {
      if (nr !== fokusLauf.current.nr) return;
      if (nachladen) setFehler(LADEFEHLER); else fokusBeenden();
    }
  }, [p.client]);
  /** Lädt das Ziel nach (Takt, Fensterfokus, nach dem Panel, nach einer Ablage); `nachBoard`: nicht, solange ein Schalten
   *  noch lädt. */
  const fokusNachladen = (nachBoard = false) => {
    const { ziel, switching } = fokusLauf.current;
    if (ziel !== null && !(nachBoard && switching)) void fokusLaden(ziel, true);
  };
  const fokusRef = useRef(fokus);
  fokusRef.current = fokus;
  // E453: Der Fokus klappt die eingeklappten Spalten der Hauptkarte und ihrer direkten Kinder auf (der Fokus-Zähler steht im Slot
  // `menue`, den me-board zugeklappt ausblendet; die Hauptkarte selbst kann nach „Im Board zeigen“ in „Erledigt“ stehen). Die
  // Spalten liegen NUR hier, nie im Gedächtnis `zu`: Das Ende des Fokus lässt sie los, und die Spalte steht wieder, wie das
  // Gedächtnis (oder Entscheid 156) sie hält. Neu gesetzt wird nur, wenn sich die Hauptkarte ändert (Einschalten, A→B), nicht
  // beim Nachladen.
  const [focusColumns, setFocusColumns] = useState<{ mainId: string | null; ids: ReadonlySet<string> }>({ mainId: null, ids: new Set() });
  if (focusColumns.mainId !== (fokus?.id ?? null)) {
    setFocusColumns({ mainId: fokus?.id ?? null, ids: new Set((fokus?.karten ?? []).map((k) => k.columnId)) });
  }
  // Klappt mit dem Ende des Fokus (oder einem Wechsel A→B) die Spalte zu, in deren Karte der Tastaturfokus steht, ginge er
  // verloren: Er wandert dann an den Klappknopf dieser Spalte (Review Nachlese 1).
  const fokusSpalteRetten = useRef<string | null>(null);
  const tastaturSpalteMerken = () => { fokusSpalteRetten.current = tastaturSpalte(sektion.current); };
  useEffect(() => {
    const id = fokusSpalteRetten.current;
    fokusSpalteRetten.current = null;
    const spalte = id === null ? undefined
      : [...(sektion.current?.querySelectorAll<HTMLElement>("me-boardspalte") ?? [])].find((x) => x.id === id);
    if (spalte === undefined || !spalte.hasAttribute("zugeklappt")) return;
    const aktiv = document.activeElement;
    if (aktiv !== null && aktiv !== document.body && !spalte.contains(aktiv)) return;
    spalte.shadowRoot?.querySelector<HTMLElement>('[part~="klapp"]')?.focus({ preventScroll: true });
  }, [focusColumns]);
  const halten = useRef<(() => void) | null>(null);
  /** Merkt die Lage der Karte `id` im Fenster; das Zeichnen danach rollt sie dorthin zurück (keine Karte springt, R26). */
  const lageHalten = (id: string) => {
    const karteEl = sektion.current?.querySelector(`[data-ticket="${id}"]`) ?? null;
    halten.current = karteEl === null ? null : keepInView(karteEl);
  };
  /** Fokus aus — Striche, „Fokus beenden“, Escape, freie Fläche, Nachladen ohne Hauptkarte: die Hauptkarte bleibt stehen,
   *  auch wenn mit dem Fokus Kinder über ihr wegfallen. */
  const fokusBeenden = () => {
    if (fokusRef.current !== null) { lageHalten(fokusRef.current.id); tastaturSpalteMerken(); }
    fokusLauf.current = { ziel: null, nr: fokusLauf.current.nr + 1, switching: false };
    setFokus(null);
  };
  const rollen = useRef<string | null>(null);
  /** Klick auf die Striche: Fokus an, aus oder auf eine andere Karte — die geklickte Karte bleibt stehen (keine springt). */
  const fokusSchalten = (id: string) => {
    rollen.current = null;
    if (fokusRef.current?.id === id) { fokusBeenden(); return; }
    lageHalten(id);
    void fokusLaden(id);
  };
  useLayoutEffect(() => { halten.current?.(); halten.current = null; }, [fokus]);
  // „Im Board zeigen" (R26): Fokus von außen; die Hauptkarte rollt nach dem Zeichnen nur ins Bild, wenn sie es nicht
  // ist (`nearest`). Öffnet das Board gerade erst, lädt der Fokus nach dem ersten Stand — einmal, nicht vorher und danach.
  const anfrageId = p.focusRequest?.id;
  const anfrageSeq = p.focusRequest?.seq;
  const boardDa = board !== null;
  useEffect(() => {
    if (anfrageId === undefined || !boardDa) return;
    rollen.current = anfrageId;
    void fokusLaden(anfrageId);
  }, [anfrageId, anfrageSeq, fokusLaden, boardDa]);

  const laden = useCallback(async () => {
    const nummer = ++lauf.current;
    try {
      const neu = await p.client.getTicketBoard(filterAnfrage(filter));
      if (nummer !== lauf.current) return;
      fokusRetten.current = fokussierteKarte(sektion.current);
      setBoard(neu);
      setFehler(null);
      // Takt, Fensterfokus, nach dem Panel: der Fokus lädt mit und bleibt (R26).
      fokusNachladen(true);
    } catch {
      if (nummer === lauf.current) setFehler(LADEFEHLER);
    }
  }, [p.client, filter, fokusLaden]);

  useEffect(() => { void laden(); }, [laden, p.neuLaden]);
  useLayoutEffect(() => {
    const id = fokusRetten.current;
    fokusRetten.current = null;
    if (id === null || sektion.current === null) return;
    const aktiv = document.activeElement;
    if (aktiv !== null && aktiv !== document.body) return;
    fokussiereKarte(sektion.current, id);
  }, [board]);
  const ladenRef = useRef(laden);
  ladenRef.current = laden;

  // Während eines Zugs lädt das Board weder im Takt noch bei Fokus-Rückkehr neu: Die Zielzone soll
  // unter dem Zeiger nicht wechseln (me-board `ziehend`, sonst die Marken des Helfers; F126).
  useEffect(() => {
    const takt = setInterval(() => { if (!zugLaeuft(boardRef.current)) void laden(); }, 60_000);
    const fokus = () => { if (!zugLaeuft(boardRef.current)) void laden(); };
    window.addEventListener("focus", fokus);
    return () => { clearInterval(takt); window.removeEventListener("focus", fokus); };
  }, [laden]);

  useEffect(() => {
    if (board === null) return undefined;
    const t = setTimeout(() => { void laden(); }, msBisMitternachtBerlin(new Date()) + 1000);
    return () => clearTimeout(t);
  }, [board?.heute, laden]);

  // Entscheid 156: Die Breite des Boards entscheidet, ob erledigte Spalten zugeklappt starten;
  // bei jeder Größenänderung neu. Am Telefon zeigt das Board ohnehin nur eine Spalte.
  const misstBoard = board !== null && wirksameAnsicht === "spalten" && !telefon;
  useEffect(() => {
    const el = boardRef.current;
    if (!misstBoard || el === null) return undefined;
    const messen = () => {
      setBoardBreite(el.clientWidth);
      const neu = bahnMasseVon(el);
      setBahn((alt) => (alt.luecke === neu.luecke && alt.rand === neu.rand ? alt : neu));
    };
    messen();
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, [misstBoard]);
  // Die Fremdspalte zählt mit (Entscheid 156): Passt sie nicht mehr daneben, startet „Erledigt" zugeklappt.
  const zuEng = !telefon && spaltenZuEng((board?.spalten.length ?? 0) + (fremdSichtbar ? 1 : 0), boardBreite, bahn);

  const filterSetzen = (f: Partial<BoardFilter>) => {
    const neu = { ...filter, ...f };
    setFilter(neu);
    merkeFilter(neu);
  };
  const ansichtSetzen = (a: "spalten" | "raster") => {
    setAnsicht(a);
    merkeAnsicht(a);
  };
  const offenSetzen = (id: string, wert: boolean) => {
    setOffen((alt) => {
      const neu = { ...alt, [id]: wert };
      merkeOffen(neu);
      return neu;
    });
  };
  const zuSetzen = (id: string, wert: boolean) => {
    setZu((alt) => {
      const neu = { ...alt, [id]: wert };
      merkeZu(neu);
      return neu;
    });
  };
  /** Der Nutzer klappt eine Spalte selbst um (E453): Sein Zustand geht ins Gedächtnis und gilt über das Ende des Fokus hinaus. */
  const columnToggled = (id: string, zugeklappt: boolean) => {
    setFocusColumns((alt) => (alt.ids.has(id) ? { ...alt, ids: new Set([...alt.ids].filter((x) => x !== id)) } : alt));
    zuSetzen(id, zugeklappt);
  };
  // Angezeigt werden die Karten des Filters und die des Fokus, die der Filter ausblendet (E434); Hauptkarte zuerst.
  const imFokus = new Map<string, "main" | "child">(
    (fokus?.karten ?? []).map((k, i) => [k.id, i === 0 ? "main" : "child"]));
  const nurFokus = (fokus?.karten ?? []).filter((k) => !(board?.tickets ?? []).some((t) => t.id === k.id));
  const angezeigt: TicketBoardCard[] = [...(board?.tickets ?? []), ...nurFokus];

  /** Legt ab; ist der Dialog „Verschieben …“ beim Eintreffen der Antwort noch offen (`imDialog`
   *  fragt das ab), kommt ein 422-Befund am Feld `uhrzeit` an ihn zurück (Feldfehler statt Hinweis
   *  über dem Board). Ist er inzwischen zu, steht der Befund wie jeder Ablagefehler am Board.
   *  Auch eine Karte, die nur der Fokus zeigt, lässt sich ablegen (E434). */
  async function ablegen(ticketId: string, ziel: AblageZiel, imDialog: () => boolean = () => false,
                         helfer?: () => boolean): Promise<string | null> {
    const t = angezeigt.find((x) => x.id === ticketId);
    if (board === null || t === undefined) return null;
    if (amEigenenPlatz(t, ziel, board.zeilen, board.heute)) return null;
    // Hat „Verschieben …“ die Uhrzeit geändert, stellt „Rückgängig“ auch sie zurück.
    const vorher = { columnId: t.columnId, resubmitDate: t.resubmitDate,
                     ...(ziel.uhrzeit !== undefined ? { resubmitTime: t.resubmitTime } : {}) };
    // Vorschau bis zur Antwort: Tag und Jetzt zeichnet die Karte sofort am Ziel; das verbindliche
    // Datum (Berliner Tag, Werktag bei Monat/später) setzt der Server (F13, F18). Eine Karte nur im Fokus rückt dort.
    const vorschau = (x: TicketBoardCard): TicketBoardCard => (x.id !== ticketId ? x : {
      ...x, columnId: ziel.columnId,
      ...(ziel.ziel === "TAG" ? { resubmitDate: ziel.datum } : ziel.ziel === "JETZT" ? { resubmitDate: board.heute } : {}),
    });
    if (board.tickets.some((x) => x.id === ticketId)) setBoard({ ...board, tickets: board.tickets.map(vorschau) });
    else setFokus((f) => (f === null ? f : { ...f, karten: f.karten.map(vorschau) }));
    // Ein Neuladen, das vor dem Ende der Ablage startete (Takt, Fokus), brächte den alten Stand
    // samt alter Version zurück (Gesamtreview M3): Der Zähler verwirft es zu Beginn und am Ende;
    // lief eins dazwischen, lädt das Board danach frisch.
    const stand = ++lauf.current;
    try {
      const neu = await p.client.ablageTicket(ticketId,
        { columnId: ziel.columnId, ziel: ziel.ziel, datum: ziel.datum, version: t.version,
          ...(ziel.uhrzeit !== undefined ? { uhrzeit: ziel.uhrzeit } : {}) });
      const dazwischen = lauf.current !== stand;
      lauf.current += 1;
      if (dazwischen) void ladenRef.current();
      setBoard((b) => (b === null ? b : { ...b, tickets: b.tickets.map((x) => (x.id === ticketId ? { ...x, ...neu } : x)) }));
      fokusNachladen();
      const spalte = board.spalten.find((s) => s.id === neu.columnId)?.name ?? "";
      const zurueck = `${neu.ticketNumber} zurück in ${board.spalten.find((s) => s.id === vorher.columnId)?.name ?? ""}`;
      setQuittung({ text: quittungsText(neu, spalte), ticketId, version: neu.version, vorher, zurueck,
                    ...(helfer !== undefined ? { helfer } : {}) });
      setHinweis(null);
      return null;
    } catch (e) {
      await laden();
      const amFeld = e instanceof ApiError && e.status === 422 && imDialog()
        ? e.fields?.find((f) => f.field === "uhrzeit")?.message : undefined;
      if (amFeld !== undefined) return amFeld;
      setHinweis(e instanceof ApiError && e.status === 409 ? GEAENDERT
        : e instanceof ApiError ? (e.fields?.[0]?.message ?? e.message) : "Das Ticket ließ sich nicht verschieben.");
      return null;
    }
  }

  const ablegenRef = useRef(ablegen);
  ablegenRef.current = ablegen;

  // Festlegung 8: alle Quittungen über `ticketReceipt` (`quittung()`: eine zur Zeit, ohne offenen Dialog am body, Klasse
  // `tq`, Rolle `status`). Die offene geht mit dem Board, wie die frühere Quittung im Board.
  const offeneQuittung = useRef<QuittungSteuerung | null>(null);
  const quittieren = (o: Parameters<typeof ticketReceipt>[0]) => {
    offeneQuittung.current = ticketReceipt({ ...o, an: sektion.current });
    return offeneQuittung.current;
  };
  useEffect(() => () => offeneQuittung.current?.schliessen(), []);

  /** Rückgängig (F23): Gegenbuchung per PATCH mit der NEUEN Version auf Spalte und Datum von vorher; danach sagt die
   *  App sie selbst an (me-design 60261ec: der Ziehhelfer schweigt mit `ansage: false`, Ruling 1d-B). */
  async function rueckgaengig(q: Quittung): Promise<void> {
    setQuittung(null);
    offeneQuittung.current?.schliessen();
    try {
      await p.client.updateTicket(q.ticketId, q.version, q.vorher);
      setHinweis(null);
      quittieren({ text: q.zurueck, name: HIERARCHY_TEXT.undoDone });
    } catch (e) {
      setHinweis(e instanceof ApiError && e.status === 409 ? GEAENDERT : "Rückgängig ist nicht gelungen.");
    }
    await ladenRef.current();
  }

  // Die Quittung einer Ablage erst nach dem Zeichnen und erst, wenn „Verschieben …“ zu ist: `quittung()` legte sie
  // sonst in den Slot dieses Dialogs, und sie ginge mit ihm. Nach 7 s, Kreuz, Escape oder einer neuen gilt ihr
  // Datensatz nicht mehr — `ziehStand.rueckgaengig` bucht dann nicht mehr gegen.
  const gezeigt = useRef<Quittung | null>(null);
  useEffect(() => {
    const q = quittung;
    if (q === null || verschieben !== null || gezeigt.current === q) return;
    gezeigt.current = q;
    // Nach einem Zug über den Helfer (Ansage, Aufleuchten, unser Rückgängig); liefert er false (ein späterer Zug war
    // der letzte) oder kam die Ablage aus „Verschieben …": direkt.
    void quittieren({ text: q.text, rueckgaengig: () => { if (q.helfer?.() !== true) void rueckgaengig(q); } })
      .geschlossen.then(() => setQuittung((alt) => (alt === q ? null : alt)));
  }, [quittung, verschieben]);

  const { bestaetigen } = useBestaetigung();
  const spaltenNameVon = (id: string) => board?.spalten.find((s) => s.id === id)?.name ?? "";
  /** E435: In eine DONE-Spalte mit offenen Kindern nur nach der Rückfrage — sonst bleibt die Karte, wo sie war. */
  const brauchtRueckfrage = (t: TicketBoardCard, ziel: AblageZiel) =>
    isOpen(t.stateKind) && board?.spalten.find((s) => s.id === ziel.columnId)?.stateKind === "DONE"
      && t.subtasks.some((z) => isOpen(z.stateKind));

  // Ziehen über die Ziehhilfen aus me-design (F126): je Ansicht ein Aufruf mit denselben Regeln
  // (`ziehOptionen`). Die Optionen lesen den aktuellen Stand über diese Ref; die Effekte laufen nur
  // beim Einhängen der Fläche (me-board bzw. Rollfeld des Rasters).
  const quittungRef = useRef(quittung);
  quittungRef.current = quittung;
  const ziehStand = useRef<ZiehStand>({ spaltenName: () => "", ablegen: () => false, rueckgaengig: () => {} });
  ziehStand.current = {
    spaltenName: spaltenNameVon,
    // Synchron: am eigenen Platz (oder ohne Ticket) false — der Zug meldet dem Helfer 'bleibt'. Mit offenen Kindern in
    // eine DONE-Spalte ebenso: Die Karte geht zurück („Weiter bearbeiten“), „Trotzdem erledigen“ legt danach ab (E435).
    ablegen: (ticketId, ziel, helfer) => {
      const t = angezeigt.find((x) => x.id === ticketId);
      if (board === null || t === undefined || amEigenenPlatz(t, ziel, board.zeilen, board.heute)) return false;
      if (brauchtRueckfrage(t, ziel)) {
        void confirmOpenChildren(bestaetigen, t.subtasks, spaltenNameVon)
          .then((ja) => { if (ja) void ablegenRef.current(ticketId, ziel); });
        return false;
      }
      void ablegen(ticketId, ziel, () => false, helfer);
      return true;
    },
    rueckgaengig: (ticketId) => {
      const q = quittungRef.current;
      if (q !== null && q.ticketId === ticketId) void rueckgaengig(q);
    },
  };
  const flaeche = board === null ? null : wirksameAnsicht;
  // E457: Am Telefon gibt es keinen Griff und kein Ziehen — `ziehbar` setzte jeder Karte ohne Griff einen eigenen ⠿ ein.
  useEffect(() => {
    const el = flaeche === "spalten" ? boardRef.current : flaeche === "raster" ? rasterFeld.current : null;
    if (el === null || telefon) return undefined;
    const zug = ziehbar(el, ziehOptionen(ziehStand, flaeche === "raster" ? "raster" : "board"));
    return () => zug.beenden();
  }, [flaeche, telefon]);

  // Nachtrag 1f (F142): die Fremdkarte; „Fälligkeit …" öffnet den Dialog rechts, angeheftete Tickets das eigene.
  const fremdKarte = (k: FremdKarte) => (
    <FremdKarteAnsicht key={k.id} k={k} heute={board!.heute} onFaelligkeit={setFremdDialog} onTicket={p.onOeffnen} />
  );
  const fremdFeiertage = [...freieTage(board?.kalender ?? []).keys()];
  async function fremdAbgleichen(): Promise<void> {
    if (p.fremd === undefined) return;
    setFremdMeldung(null);
    try { await p.fremd.syncFremdtickets(); }
    catch (f) { setFremdMeldung(f instanceof ApiError ? f.message : "Der Abgleich ist gescheitert."); }
    fremdStand.neu();
  }
  const shown = new Set(angezeigt.map((t) => t.id));
  const titelVon = new Map(angezeigt.map((t) => [t.id, t.title]));
  const karte = (t: TicketBoardCard) => (
    <TicketKarteAnsicht key={t.id} t={t} heute={board!.heute} ichId={p.ich.id} companies={p.companies}
      onOeffnen={p.onOeffnen} onVerschieben={setVerschieben} shown={shown} focusRole={imFokus.get(t.id) ?? null}
      focused={fokus?.id === t.id} onToggleFocus={fokusSchalten}
      parentTitle={t.parentTicketId === null ? null : titelVon.get(t.parentTicketId) ?? null}
      {...(p.onShowInBoard === undefined ? {} : { onShowInBoard: p.onShowInBoard })} />
  );
  // Ein Tag am Wochenende oder Feiertag bleibt Ziel, wird aber als Warnung getönt (119).
  const zielAttribute = (z: AblageZiel): Record<string, string> =>
    ablageAttribute(z, z.ziel === "TAG" && z.datum !== null ? popupFuerTag(z.datum, board?.kalender ?? []) : "");

  const offeneAnzahl = (board?.tickets ?? []).filter((t) => t.stateKind !== "DONE").length;
  // F129: Der Zähler steht am Reiter „Board“ (Bereich), nicht mehr in einer Überschrift (199b). Der
  // Rückruf über eine Ref: ein neuer Rückruf allein meldet nicht noch einmal.
  const onOffenRef = useRef(p.onOffen);
  onOffenRef.current = p.onOffen;
  const heuteStand = board?.heute ?? null;
  useEffect(() => {
    if (heuteStand !== null) onOffenRef.current?.(offeneAnzahl, heuteStand);
  }, [offeneAnzahl, heuteStand]);

  // „Im Board zeigen": rollen, sobald Fokus und Karte gezeichnet sind. Am Telefon zeigt das Board nur eine Spalte: erst auf
  // die Spalte der Hauptkarte schalten, sonst stünde sie unsichtbar daneben (Abschluss-Review I1, R27).
  useLayoutEffect(() => {
    const id = rollen.current;
    if (id === null || fokus === null || fokus.id !== id) return;
    const spalte = fokus.karten[0]?.columnId;
    if (telefon && spalte !== undefined && spalte !== aktiveTelefonSpalte) {
      if (board?.spalten.some((s) => s.id === spalte) === true) setTelefonSpalte(spalte);
      return;
    }
    const el = sektion.current?.querySelector(`[data-ticket="${id}"]`) ?? null;
    if (el === null) return;
    rollen.current = null;
    el.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [fokus, board, telefon, aktiveTelefonSpalte]);
  // R26: Escape und ein Klick auf freie Boardfläche beenden den Fokus. Native Hörer an der Sektion statt React-Ereignisse:
  // Die liefen auch aus der Randliste (Portal am body) hierher. Ein Escape, das ein Dialog schon verbraucht hat
  // (`preventDefault`, me-dialog), gehört dorthin.
  useEffect(() => {
    const el = sektion.current;
    if (el === null) return undefined;
    const frei = (e: Event) => !e.composedPath().some((n) => n instanceof Element && n.matches(NICHT_FREI));
    const klick = (e: MouseEvent) => { if (fokusRef.current !== null && frei(e)) fokusBeenden(); };
    const taste = (e: KeyboardEvent) => {
      if (e.key === "Escape" && fokusRef.current !== null && !e.defaultPrevented) fokusBeenden();
    };
    el.addEventListener("click", klick);
    el.addEventListener("keydown", taste);
    return () => { el.removeEventListener("click", klick); el.removeEventListener("keydown", taste); };
  }, []);

  return (
    // tabIndex -1: Fokus-Ersatz nach dem Schließen des Panels, wenn auch der Reiter keinen nimmt (F129).
    <section className="tb" aria-label="Tickets" ref={sektion} tabIndex={-1}
      {...(fokus !== null ? { "data-focus": "" } : {})}>
      {/* F129 (Entscheid 199, me-design 61 Variante C): Filter links in benannten Gruppen, rechts die
          Darstellung und „Neue Spalte“, ganz rechts „+ Aufgabe“. Stufen nach der eigenen Breite, „…“,
          Telefonblatt (Trichter mit Zähler, Name „Filter, n aktiv“; „+ Aufgabe“ dort als Plus-Symbolknopf, 68) und
          „Zurücksetzen“ macht das Element; Beschriftungen bleiben aus
          (Vorgabe). Die Filter bleiben Kinder der App, auch im Telefonblatt (der gewählte Kunde behält
          seinen Namen). Am Telefon gibt es weder Raster (F36) noch „Neue Spalte“ (154): Der Slot bleibt
          leer, die Leiste zeigt dann kein „…“. Spaltenpflege steht hier, weil me-board nur Spalten
          als Kinder nimmt (Task 18). */}
      <Werkzeugleiste className="tb__leiste" aria-label="Werkzeuge des Boards" aktiv={anzahlAktiverFilter(filter)}
        onZurueckgesetzt={() => filterSetzen(LEERER_FILTER)}
        filter={(
          <TicketFilterleiste client={p.client} companies={p.companies} ich={p.ich} tickets={board?.tickets ?? []}
            filter={filter} onFilter={filterSetzen} />
        )}
        aktionen={telefon ? undefined : (
          <>
            <Werkzeuggruppe beschriftung="Darstellung" ueberlauf={2}>
              <Optionen aria-label="Darstellung" darstellung="segmente" richtung="waagerecht" wert={ansicht}
                onGeaendert={(d) => ansichtSetzen(d.wert === "raster" ? "raster" : "spalten")}>
                <option value="spalten" aria-label="je Spalte" data-symbol={SYMBOL_JE_SPALTE}>je Spalte</option>
                <option value="raster" aria-label="Raster" data-symbol={SYMBOL_RASTER}>Raster</option>
              </Optionen>
            </Werkzeuggruppe>
            <Knopf art="leise" ueberlauf={1} symbol={SYMBOL_NEUE_SPALTE}
              onClick={() => setSpaltenDialog({ spalte: null })}>
              Neue Spalte
            </Knopf>
          </>
        )}
        haupt={(
          // Mit Symbol: am Telefon ein Symbolknopf (me-design 493c8d6, Entscheid 68), sonst der Text.
          <Knopf art="primaer" symbol={SYMBOL_PLUS}
            onClick={() => p.onAnlegen(filter.company === "ALLE" ? null : filter.company)}>
            + Aufgabe
          </Knopf>
        )} />
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      {hinweis !== null && <Hinweis art="warnung">{hinweis}</Hinweis>}
      {board !== null && wirksameAnsicht === "raster" && (
        <div className="tb__raster" ref={rasterFeld}>
          <TicketRaster board={{ ...board, tickets: angezeigt }} karte={karte} zielAttribute={zielAttribute}
            {...(fremdSichtbar ? { fremd: { karten: fremdStand.daten!.items, karte: fremdKarte } } : {})} />
        </div>
      )}
      {board !== null && wirksameAnsicht === "spalten" && (
        // Am Telefon zeigt das Element genau eine Spalte und darüber seine Segmentleiste (Name und
        // Zähler); `aktiv` hält die gewählte Spalte, fehlt sie, gilt der Eingang.
        <MeBoard ref={boardRef} className="tb__board" aria-label="Spalten" spaltenbreiteMin={320}
          spaltenbreiteMax={640} aktiv={aktiveTelefonSpalte} onSpalteGewaehlt={(d) => setTelefonSpalte(d.id)}>
          {/* Nachtrag 1f (F142, Frage 225): die Fremdspalte ganz links — kein Ziel, keine Spaltenpflege. */}
          {fremdSichtbar && (
            <FremdSpalte daten={fremdStand.daten!} zeilen={board.zeilen} heute={board.heute}
              zugeklappt={zu[FREMD_SPALTE_ID] ?? false} onUmgeschaltet={(z) => zuSetzen(FREMD_SPALTE_ID, z)}
              karte={fremdKarte} gesperrtBis={fremdStand.daten!.naechsterManuellAb}
              {...(fremdPersonId === p.ich.id ? { onAbgleich: () => { void fremdAbgleichen(); } } : {})} />
          )}
          {board.spalten.map((s) => {
            const tickets = angezeigt.filter((t) => t.columnId === s.id);
            const ueberfaellig = tickets.filter((t) => t.resubmitDate !== null && t.resubmitDate < board.heute
                                                     && s.timelineMode !== "NONE").length;
            const istOffen = offen[s.id] ?? s.timelineMode === "EXPANDED";
            // R26, Festlegung 7: die Karten im Fokus, „+n“ für die, die nur der Fokus zeigt; am Telefon zählt die
            // Spaltenwahl sie in Klammern (R27).
            const fokusHier = tickets.filter((t) => imFokus.has(t.id)).length;
            const nurFokusHier = tickets.filter((t) => nurFokus.some((k) => k.id === t.id)).length;
            const fokusText = HIERARCHY_TEXT.focusCounter(fokusHier)
              + (nurFokusHier > 0 ? ` ${HIERARCHY_TEXT.focusExtra(nurFokusHier)}` : "");
            return (
              <Boardspalte key={s.id} id={s.id} name={s.name}
                zaehler={telefon && fokusHier > 0 ? HIERARCHY_TEXT.focusColumnCount(tickets.length, fokusHier)
                  : tickets.length}
                fehler={ueberfaellig} fehlerText="überfällig" randlos
                zugeklappt={!telefon && focusColumns.ids.has(s.id) ? false : zu[s.id] ?? (zuEng && startetZu(s))}
                onUmgeschaltet={(d) => columnToggled(s.id, d.zugeklappt)}
                menue={(
                  <>
                    {fokusHier > 0 && <Chip className="tb__fokuszaehler">{fokusText}</Chip>}
                    <KiKnopf art="leise" className="tb__spaltenmenue" name={`Spalte ${s.name} bearbeiten`}
                      onClick={() => setSpaltenDialog({ spalte: s })}>⋯</KiKnopf>
                  </>
                )}>
                <TicketZeitleiste spalte={s} zeilen={board.zeilen} heute={board.heute} tickets={tickets}
                  kalender={board.kalender} offen={istOffen} karte={karte} zielAttribute={zielAttribute} />
              </Boardspalte>
            );
          })}
        </MeBoard>
      )}
      {verschieben !== null && board !== null && (
        <TicketVerschieben t={verschieben} spalten={board.spalten} zeilen={board.zeilen} heute={board.heute}
          kalender={board.kalender} onAbbrechen={() => setVerschieben(null)}
          onAblage={async (z) => {
            // Der Dialog bleibt offen, bis die Ablage steht: Ein Befund an der Uhrzeit gehört an
            // ihr Feld (F93); alles andere meldet das Board wie beim Ziehen. E435: „Weiter bearbeiten“ lässt ihn
            // offen und legt nichts ab.
            const id = verschieben.id;
            const t = angezeigt.find((x) => x.id === id);
            if (t !== undefined && brauchtRueckfrage(t, z)
                && !(await confirmOpenChildren(bestaetigen, t.subtasks, spaltenNameVon))) return null;
            const fehler = await ablegenRef.current(id, z, () => verschiebenOffen.current === id);
            if (fehler === null) setVerschieben(null);
            return fehler;
          }} />
      )}
      {spaltenDialog !== null && board !== null && (
        // Die Spalte aus dem frischen Stand (Version und Lage nach einem Neuladen); der
        // Dialog selbst bleibt beim Neuladen stehen (Hausregel), seine Eingaben auch.
        <SpaltenDialog client={p.client} spalten={board.spalten}
          spalte={spaltenDialog.spalte === null ? null
            : board.spalten.find((s) => s.id === spaltenDialog.spalte!.id) ?? spaltenDialog.spalte}
          dichteOffen={spaltenDialog.spalte === null ? undefined
            : offen[spaltenDialog.spalte.id] ?? spaltenDialog.spalte.timelineMode === "EXPANDED"}
          onDichte={(wert) => { if (spaltenDialog.spalte !== null) offenSetzen(spaltenDialog.spalte.id, wert); }}
          onAbbrechen={() => setSpaltenDialog(null)} onNeuLaden={laden}
          onFertig={() => { setSpaltenDialog(null); void laden(); }} />
      )}
      {fremdMeldung !== null && <Hinweis art="warnung" schliessbar onGeschlossen={() => setFremdMeldung(null)}>{fremdMeldung}</Hinweis>}
      {fremdDialog !== null && p.fremd !== undefined && (
        <FremdDialog client={p.fremd} id={fremdDialog} heute={board?.heute ?? berlinerTag(new Date().toISOString())}
          feiertage={fremdFeiertage} {...(fremdPersonId === p.ich.id ? { zustaendig: p.ich.name } : {})}
          onSchliessen={(geaendert) => { setFremdDialog(null); if (geaendert) fremdStand.neu(); }} />
      )}
    </section>
  );
}
